import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useOutletContext, useSearchParams } from "react-router";
import { AppShell } from "../components/AppShell";
import { DivisionChips } from "../components/DivisionChips";
import { PageHeader } from "../components/PageHeader";
import { SubNav } from "../components/SubNav";
import { FreshnessStamp } from "../components/ui";
import { usePeopleOverview } from "../lib/api";
import Assignments from "./people/Assignments";
import Compliance from "./Compliance";
import Conduct from "./people/Conduct";
import Files from "./people/Files";
import EmployeeList from "./EmployeeList";
import PersonTimeline from "./people/PersonTimeline";
import Workforce from "./Workforce";

/** People experience (WP-11, WP-23, PPL-01 … PPL-19): a module shell with one addressable screen per area —
 * directory, lifecycle, recruitment, workforce search and redeployment, compliance repository, and the L&D
 * roadmap. Each screen is a route, so it can be linked to, bookmarked and named by a breadcrumb; the company
 * filter lives in the query string for the same reason. */

export const PEOPLE_SCREENS = ["directory", "lifecycle", "leave", "expenses", "payroll", "appraisals", "conduct", "files", "recruitment", "workforce", "compliance", "learning", "assignments"] as const;

/** Where the pre-2026-09 `?tab=` links land. Kept so existing bookmarks, the demo scripts and any link already
 * sent to the customer still work; remove one release after the screens ship. */
export const TAB_REDIRECT: Record<string, string> = {
  list: "directory", hr: "lifecycle", rec: "recruitment",
  wf: "workforce", compliance: "compliance", ld: "learning",
};

type PeopleContext = { company: string };

/** The company filter, shared by every People screen. */
export function usePeopleCompany(): string {
  return useOutletContext<PeopleContext>().company;
}

export default function People() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const company = params.get("company") ?? "";
  const ov = usePeopleOverview(company || undefined);
  const all = usePeopleOverview(undefined);
  const companies = useMemo(() => (all.data?.headcount ?? []).map((h) => h.company).sort(), [all.data]);

  const setCompany = (next: string) => {
    const q = new URLSearchParams(params);
    if (next) q.set("company", next);
    else q.delete("company");
    setParams(q, { replace: true });
  };

  // the filter travels with the reader from screen to screen, and stays in the URL so the filtered view is shareable
  const query = company ? `?company=${encodeURIComponent(company)}` : "";
  const items = PEOPLE_SCREENS.map((k) => ({ key: k, to: `/people/${k}${query}`, label: t(`ppl.tabs.${k}`) }));

  return (
    <AppShell subtitle={t("tiles.people.title")}>
      <PageHeader title={t("ppl.title")} subtitle={t("ppl.subtitle")}
        stamp={ov.data ? <FreshnessStamp lastUpdated={ov.data.as_of} /> : undefined} />
      {companies.length > 1 && (
        <div className="mt-4">
          <DivisionChips companies={companies} value={company} onChange={setCompany} allLabel={t("exec.allDivisions")} />
        </div>
      )}
      <SubNav items={items} label={t("ppl.sections")} />
      <div className="mt-2"><Outlet context={{ company } satisfies PeopleContext} /></div>
    </AppShell>
  );
}

/** `/people` itself: send the reader to the directory, or to whichever screen an old `?tab=` link meant. */
export function PeopleIndex() {
  const [params] = useSearchParams();
  const screen = TAB_REDIRECT[params.get("tab") ?? ""] ?? "directory";
  const rest = new URLSearchParams(params);
  rest.delete("tab");
  const query = rest.toString();
  return <Navigate to={`/people/${screen}${query ? `?${query}` : ""}`} replace />;
}

export function PeopleDirectory() {
  return <EmployeeList company={usePeopleCompany() || undefined} />;
}
export function PeopleWorkforce() {
  return <Workforce company={usePeopleCompany() || undefined} />;
}
export function PeopleFiles() {
  return <Files company={usePeopleCompany()} />;
}
export function PeopleConduct() {
  return <Conduct company={usePeopleCompany()} />;
}
export function PeopleCompliance() {
  return <Compliance company={usePeopleCompany() || undefined} />;
}
export function PeopleAssignments() {
  return <Assignments company={usePeopleCompany() || undefined} />;
}
export function PeoplePerson() {
  return <PersonTimeline />;
}
